import { useRef, useCallback, useState } from 'react';
import * as Cesium from 'cesium';
import type { GroundStation, Strategy, GeoFeature, Satellite, AccessWindowPair } from '../../types/mission';
import type { SensorData } from '../../types/sensor';
import type { LayerState } from '../../types/mdai';
import type { SatelliteGroupDataType } from '../../../../modelling/types';
import type { KeplerianOrbitParams } from '../../lib/orbitPropagator';

// Utilities
import { useCartesianUtils } from './helpers/cartesianUtils';
import { useAOIAndFOV } from './helpers/aoiAndFov';
import { useEntityGroups } from './helpers/entityGroups';

// Features
import { useSatelliteOrbits } from './features/satelliteOrbits';
import { useVisualizationControls } from './features/visualization';
import { useCameraControls } from './features/camera';
import { useGroundStations } from './features/groundStations';
import { useSensorFov } from './features/sensorFov';
import { useLineOfSight } from './features/lineOfSight';
import { useGroundTracks } from './features/groundTracks';
import { useGeoFeatures } from './features/geoFeatures';
import { useHighlights } from './features/highlights';
import { useMeasurement } from './features/measurement';
import { useOrbitPreview } from './features/orbitPreview';
import { useMapTheme } from './features/mapTheme';
import { useOrbitTiming } from './features/orbitTiming';
import { useCoverageVisualization } from './features/coverageVisualization';
import { useGraticule } from './features/graticule';

// Viewers
import { usePrimaryViewer } from './viewers/primaryViewer';
import { useSecondaryViewer } from './viewers/secondaryViewer';

// Clock
import { useClockManager } from './clock/clockManager';

// Constants & Types
const defaultLayers_: LayerState = {
  satellites: true, geofeatures: true, ground: true, trails: true,
  groundTracks: false, sensorFovs: false, gsFov: false,
  los: false, losSatGS: false, labels: false,
};

export function useCesiumMap() {
  // ─── Refs ────────────────────────────────────────────────────────────────
  const viewerRef = useRef<Cesium.Viewer | null>(null);
  const secondaryViewerRef = useRef<Cesium.Viewer | null>(null);
  const sharedDataSourceRef = useRef<any>(null);
  const previewDataSourceRef = useRef<any>(null);
  const highlightDataSourceRef = useRef<any>(null);
  const selectionDataSourceRef = useRef<any>(null);
  const measureDataSourceRef = useRef<any>(null);
  const clockSyncRemoveRef = useRef<Cesium.Event.RemoveCallback | null>(null);
  const cameraSyncRemoveRef = useRef<Cesium.Event.RemoveCallback | null>(null);

  const secondarySharedDataSourceRef = useRef<any>(null);
  const secondaryHighlightDataSourceRef = useRef<any>(null);
  const secondarySelectionDataSourceRef = useRef<any>(null);
  const secondaryPreviewDataSourceRef = useRef<any>(null);
  const secondaryMeasureDataSourceRef = useRef<any>(null);

  const satelliteFollowRemoveRef = useRef<Cesium.Event.RemoveCallback | null>(null);
  const satelliteFollowTokenRef = useRef(0);
  const satelliteFollowSatIdRef = useRef<string | null>(null);
  const satelliteFollowDistanceRef = useRef<number | null>(null);

  const orbitLeadTimeRef = useRef<number>(55 * 60);
  const orbitTrailTimeRef = useRef<number>(0);
  const modelClockWindowRef = useRef<{ startEpoch: number; endEpoch: number } | null>(null);

  const entityGroupMap = useRef<Map<string, string[]>>(new Map());
  const satActiveMap = useRef<Record<string, boolean>>({});
  const gsActiveMap = useRef<Record<string, boolean>>({});
  const gsImagePropsRef = useRef<Map<string, Cesium.ConstantProperty>>(new Map());
  const gsConnectionTypeRef = useRef<Map<string, 'bidirectional' | 'unidirectional'>>(new Map());
  const geoFeatureActiveMap = useRef<Record<string, boolean>>({});
  const geoPointImagePropsRef = useRef<Map<string, Cesium.ConstantProperty>>(new Map());
  const geoOutlineColorPropsRef = useRef<Map<string, Cesium.ConstantProperty>>(new Map());
  const geoFillColorPropsRef = useRef<Map<string, Cesium.ConstantProperty>>(new Map());
  const satOrbitActiveMap = useRef<Record<string, boolean>>({});
  const layersRef = useRef<LayerState>(defaultLayers_);
  const losVisibilityHoldRef = useRef<Map<string, number>>(new Map());
  const groundTrackTickSubs = useRef<Map<string, Cesium.Event.RemoveCallback>>(new Map());
  const editingSatelliteIdsRef = useRef<Set<string>>(new Set());

  const previewGenRef = useRef(0);
  const gridVisibleRef = useRef<boolean>(false);
  const accessWindowPairsRef = useRef<any[]>([]);
  const satIdsRef = useRef<string[]>([]);

  const measureHandlerRef = useRef<Cesium.ScreenSpaceEventHandler | null>(null);
  const measureStepRef = useRef<0 | 1>(0);
  const measurePtARef = useRef<Cesium.Cartographic | null>(null);

  const drawingHandlerRef = useRef<Cesium.ScreenSpaceEventHandler | null>(null);
  const polygonVerticesRef = useRef<{ lat: number; lng: number }[]>([]);
  const draftVertexEntityIdsRef = useRef<string[]>([]);
  const circleCenterRef = useRef<{ lat: number; lng: number } | null>(null);
  const circleRadiusRef = useRef<number>(0);
  const draggingVertexIdxRef = useRef<number | null>(null);

  // ─── State ───────────────────────────────────────────────────────────────
  const [cursorLatLon, setCursorLatLon] = useState<{ lat: number; lon: number } | null>(null);
  const [measureDistanceKm, setMeasureDistanceKm] = useState<number | null>(null);
  const [liveDrawingDistanceKm, setLiveDrawingDistanceKm] = useState<number | null>(null);

  // ─── Initialize all feature hooks ─────────────────────────────────────────
  const cartesianUtils = useCartesianUtils(viewerRef, modelClockWindowRef);
  const aoiAndFov = useAOIAndFOV(viewerRef, sharedDataSourceRef);
  const entityGroups = useEntityGroups(viewerRef, sharedDataSourceRef, entityGroupMap, losVisibilityHoldRef);

  const satOrbits = useSatelliteOrbits(
    viewerRef,
    sharedDataSourceRef,
    modelClockWindowRef,
    entityGroupMap,
    satOrbitActiveMap,
    layersRef,
    orbitLeadTimeRef,
    orbitTrailTimeRef,
    entityGroups.clearGroup,
    cartesianUtils.getDefaultModelEpoch,
    editingSatelliteIdsRef,
  );

  const visualization = useVisualizationControls(
    viewerRef,
    secondaryViewerRef,
    layersRef,
    satActiveMap,
    gsActiveMap,
    geoFeatureActiveMap,
    sharedDataSourceRef,
    secondarySharedDataSourceRef,
  );

  const camera = useCameraControls(
    viewerRef,
    secondaryViewerRef,
    sharedDataSourceRef,
    satelliteFollowSatIdRef,
    satelliteFollowDistanceRef,
    satelliteFollowTokenRef,
    satelliteFollowRemoveRef,
  );

  const groundStations = useGroundStations(
    viewerRef,
    secondaryViewerRef,
    sharedDataSourceRef,
    highlightDataSourceRef,
    entityGroupMap,
    gsActiveMap,
    gsImagePropsRef,
    gsConnectionTypeRef,
    layersRef,
    aoiAndFov.isSurfacePointCameraFacing,
  );

  const sensorFov = useSensorFov(
    viewerRef,
    secondaryViewerRef,
    sharedDataSourceRef,
    previewDataSourceRef,
    entityGroupMap,
    satOrbitActiveMap,
    layersRef,
    entityGroups.clearGroup,
  );

  const lineOfSight = useLineOfSight(
    viewerRef,
    secondaryViewerRef,
    sharedDataSourceRef,
    entityGroupMap,
    layersRef,
    losVisibilityHoldRef,
    satOrbitActiveMap,
    entityGroups.clearGroup,
  );

  const groundTracks = useGroundTracks(
    viewerRef,
    secondaryViewerRef,
    sharedDataSourceRef,
    modelClockWindowRef,
    orbitLeadTimeRef,
    orbitTrailTimeRef,
    entityGroupMap,
    satOrbitActiveMap,
    layersRef,
    groundTrackTickSubs,
    entityGroups.clearGroup,
  );

  const geoFeatures = useGeoFeatures(
    viewerRef,
    secondaryViewerRef,
    sharedDataSourceRef,
    entityGroupMap,
    layersRef,
    drawingHandlerRef,
    polygonVerticesRef,
    circleCenterRef,
    circleRadiusRef,
    draftVertexEntityIdsRef,
    draggingVertexIdxRef,
    setLiveDrawingDistanceKm,
    cartesianUtils.pickCartesian,
    entityGroups.clearGroup,
    geoPointImagePropsRef,
    geoOutlineColorPropsRef,
    geoFillColorPropsRef,
    geoFeatureActiveMap,
    aoiAndFov.isSurfacePointCameraFacing,
    accessWindowPairsRef,
    satIdsRef,
  );

  const highlights = useHighlights(
    viewerRef,
    sharedDataSourceRef,
    highlightDataSourceRef,
    selectionDataSourceRef,
    gsImagePropsRef,
    gsConnectionTypeRef,
    gsActiveMap,
    geoPointImagePropsRef,
    geoOutlineColorPropsRef,
    geoFillColorPropsRef,
    satelliteFollowTokenRef,
    satelliteFollowSatIdRef,
    satelliteFollowDistanceRef,
    satelliteFollowRemoveRef,
    orbitLeadTimeRef,
    orbitTrailTimeRef,
    satOrbitActiveMap,
    layersRef,
  );

  const measurement = useMeasurement(
    viewerRef,
    measureDataSourceRef,
    measureHandlerRef,
    measureStepRef,
    measurePtARef,
    setMeasureDistanceKm,
  );

  const orbitPreview = useOrbitPreview(
    viewerRef,
    sharedDataSourceRef,
    entityGroupMap,
    layersRef,
    orbitLeadTimeRef,
    orbitTrailTimeRef,
    entityGroups.clearGroup,
  );

  const mapTheme = useMapTheme(viewerRef, secondaryViewerRef);

  const orbitTiming = useOrbitTiming(
    orbitLeadTimeRef,
    orbitTrailTimeRef,
    viewerRef,
    secondaryViewerRef,
  );

  const coverageVisualization = useCoverageVisualization(viewerRef, sharedDataSourceRef);
  const graticule = useGraticule(viewerRef, secondaryViewerRef, gridVisibleRef);

  const clock = useClockManager(
    viewerRef,
    secondaryViewerRef,
    sharedDataSourceRef,
    modelClockWindowRef,
    cartesianUtils.getDefaultModelEpoch,
  );

  const primaryViewer = usePrimaryViewer(
    viewerRef,
    secondaryViewerRef,
    sharedDataSourceRef,
    secondarySharedDataSourceRef,
    previewDataSourceRef,
    secondaryPreviewDataSourceRef,
    highlightDataSourceRef,
    secondaryHighlightDataSourceRef,
    selectionDataSourceRef,
    secondarySelectionDataSourceRef,
    measureDataSourceRef,
    secondaryMeasureDataSourceRef,
    setCursorLatLon,
    gsActiveMap,
    geoPointImagePropsRef,
    secondaryHighlightDataSourceRef,
    cartesianUtils.pickCartesian,
  );

  const secondaryViewer = useSecondaryViewer(
    viewerRef,
    secondaryViewerRef,
    clockSyncRemoveRef,
    cameraSyncRemoveRef,
    secondarySharedDataSourceRef,
    secondaryHighlightDataSourceRef,
    secondarySelectionDataSourceRef,
    secondaryPreviewDataSourceRef,
    secondaryMeasureDataSourceRef,
    sharedDataSourceRef,
    highlightDataSourceRef,
    selectionDataSourceRef,
    previewDataSourceRef,
    measureDataSourceRef,
    mapTheme.mapTheme,
  );

  const setSceneMode = useCallback((mode: 'SCENE3D' | 'SCENE2D') => {
    visualization.setSceneMode(mode);
    // Redraw draft ground station so that is2D check evaluates correctly for Columbus View/SCENE2D
    setTimeout(() => {
      groundStations.redrawDraftGroundStation();
    }, 50);
  }, [visualization, groundStations]);

  // ─── Return unified API ──────────────────────────────────────────────────
  return {
    // Internal refs (for backward compatibility)
    viewerRef,
    secondaryViewerRef,

    // Viewer
    initViewer: primaryViewer.initViewer,
    initSecondaryViewer: useCallback((el: HTMLDivElement) => {
      secondaryViewer.initSecondaryViewer(el);
      if (gridVisibleRef.current) {
        graticule.setGridVisible(true);
      }
    }, [secondaryViewer, graticule]),
    destroySecondaryViewer: secondaryViewer.destroySecondaryViewer,

    // Cartesian & Geometry
    pickCartesian: cartesianUtils.pickCartesian,
    getDefaultModelEpoch: cartesianUtils.getDefaultModelEpoch,
    clampEpochToModelWindow: cartesianUtils.clampEpochToModelWindow,

    // AOI & FOV
    addAOI: aoiAndFov.addAOI,
    isSurfacePointCameraFacing: aoiAndFov.isSurfacePointCameraFacing,

    // Entity Groups
    clearGroup: entityGroups.clearGroup,

    // Satellite Orbits
    drawSatelliteGroupOrbits: satOrbits.drawSatelliteGroupOrbits,
    setSatOrbitVisible: satOrbits.setSatOrbitVisible,
    setGroupOrbitVisible: satOrbits.setGroupOrbitVisible,
    setEditingSatelliteIds: useCallback((ids: Set<string>) => {
      editingSatelliteIdsRef.current = ids;
      viewerRef.current?.scene.requestRender();
    }, []),

    // Orbit Preview
    showOrbitPreview: orbitPreview.showOrbitPreview,
    showTleOrbitPreview: orbitPreview.showTleOrbitPreview,
    clearOrbitPreview: orbitPreview.clearOrbitPreview,
    drawOrbits: orbitPreview.drawOrbits,

    // Ground Stations
    addGroundStations: groundStations.addGroundStations,
    addOneGroundStation: groundStations.addOneGroundStation,
    removeGroundStation: groundStations.removeGroundStation,
    updateOneGroundStation: groundStations.updateOneGroundStation,
    removeDraftGroundStation: groundStations.removeDraftGroundStation,
    updateDraftGroundStation: groundStations.updateDraftGroundStation,
    redrawDraftGroundStation: groundStations.redrawDraftGroundStation,
    displayGsContactRings: groundStations.displayGsContactRings,
    clearAllGsRings: groundStations.clearAllGsRings,
    addGsRingsForPosition: groundStations.addGsRingsForPosition,

    // Visualization
    updateLayers: visualization.updateLayers,
    setSatActive: visualization.setSatActive,
    setGSActive: visualization.setGSActive,
    setGeoFeatureVisible: visualization.setGeoFeatureVisible,
    setSceneMode,
    toggleLighting: visualization.toggleLighting,

    // Camera
    zoomIn: camera.zoomIn,
    zoomOut: camera.zoomOut,
    resetZoom: camera.resetZoom,
    getZoomLevel: camera.getZoomLevel,
    flyToOrbitView: camera.flyToOrbitView,
    centerOnSatellite: camera.centerOnSatellite,
    centerOnPoint: camera.centerOnPoint,
    flyToSatellite: camera.flyToSatellite,
    syncSecondaryCamera: camera.syncSecondaryCamera,
    viewWholeEarth2D: camera.viewWholeEarth2D,

    // Sensor FOV
    drawSensorFovs: sensorFov.drawSensorFovs,
    showSensorPreview: sensorFov.showSensorPreview,
    clearSensorPreview: sensorFov.clearSensorPreview,
    clearSensorFovs: sensorFov.clearSensorFovs,
    flyToSensorPreview: camera.flyToSatellite, // Placeholder

    // Line of Sight
    drawLOS: (
      groups: any[],
      gsList: any[],
      focusedSatId?: string | null,
      accessWindowPairs: any[] = [],
      geoList: any[] = [],
    ) => {
      accessWindowPairsRef.current = accessWindowPairs;
      const ids: string[] = [];
      groups.forEach((g) =>
        g.satellites.forEach((s: any) => {
          if ((s.orbitParameters?.semiMajorAxis ?? 0) > 0) ids.push(s.id);
        }),
      );
      satIdsRef.current = ids;
      lineOfSight.drawLOS(groups, gsList, focusedSatId, accessWindowPairs, geoList);
    },
    clearLOS: lineOfSight.clearLOS,

    // Ground Tracks
    drawGroundTracks: groundTracks.drawGroundTracks,
    clearGroundTracks: groundTracks.clearGroundTracks,

    // GeoFeatures
    stopDraw: geoFeatures.stopDraw,
    startPointDraw: geoFeatures.startPointDraw,
    updateDraftPoint: geoFeatures.updateDraftPoint,
    startPolygonDraw: geoFeatures.startPolygonDraw,
    updateDraftPolygon: geoFeatures.updateDraftPolygon,
    startCircleDraw: geoFeatures.startCircleDraw,
    updateDraftCircle: geoFeatures.updateDraftCircle,
    startPolygonVertexEdit: geoFeatures.startPolygonVertexEdit,
    loadPolygonForEdit: geoFeatures.loadPolygonForEdit,
    commitGeoFeature: geoFeatures.commitGeoFeature,
    removeGeoFeature: geoFeatures.removeGeoFeature,

    // Highlights & Selection
    highlightSatellite: highlights.highlightSatellite,
    highlightGroundStation: highlights.highlightGroundStation,
    highlightGeoFeature: highlights.highlightGeoFeature,
    setSelectedSatellite: highlights.setSelectedSatellite,
    setSelectedGroundStations: highlights.setSelectedGroundStations,
    setSelectedGeoFeatures: highlights.setSelectedGeoFeatures,
    stopFollowingSatellite: highlights.stopFollowingSatellite,

    // Measurement
    startMeasureMode: measurement.startMeasureMode,
    stopMeasureMode: measurement.stopMeasureMode,

    // Clock
    setClockPlaying: clock.setClockPlaying,
    setClockMultiplier: clock.setClockMultiplier,
    resetClockToNow: clock.resetClockToNow,
    resetClockToModelStart: clock.resetClockToModelStart,
    getLiveSatelliteState: clock.getLiveSatelliteState,
    initClockFromModel: clock.initClockFromModel,
    setClockTime: clock.setClockTime,

    // Coverage Visualization
    renderCoveragePasses: coverageVisualization.renderCoveragePasses,
    clearCoverageVisualization: coverageVisualization.clearCoverageVisualization,

    // Map Theme
    mapTheme: mapTheme.mapTheme,
    setMapTheme: mapTheme.setMapTheme,

    // Orbit Timing
    setOrbitLeadTime: orbitTiming.setOrbitLeadTime,
    setOrbitTrailTime: orbitTiming.setOrbitTrailTime,

    // Graticule Grid
    setGridVisible: graticule.setGridVisible,
    isGridVisible: () => gridVisibleRef.current,

    // State / Refs
    orbitLeadTimeRef,
    orbitTrailTimeRef,
    cursorLatLon,
    measureDistanceKm,
    liveDrawingDistanceKm,
  };
}
